import { Alert, Button, Link, Spinner } from '@1k5-tech-es/grado-react';
import { Eye, Mail, RotateCw, Square } from 'lucide-react';
import { Link as RouterLink } from 'react-router';

import type { EmailAdvisor, EmailOverview } from '../../api/types';
import { formatDateTime } from '../../lib/format';
import { describeEmailError, planSend, plural, sendProgress, STATUS_LABEL } from './emails';
import { TestModeBanner } from './SendConfirmDialog';

/**
 * Step 4: send everyone their report, follow the slow send as it goes, and
 * see who did not get it (with the reason) to retry or send it by hand.
 */
export function EmailPanel({
  overview,
  onSendAll,
  onStop,
  onRetry,
  onPreview,
  stopping,
  retryingId,
}: {
  overview: EmailOverview | undefined;
  onSendAll: () => void;
  onStop: () => void;
  onRetry: (emailId: number) => void;
  onPreview: (advisorId: number) => void;
  stopping: boolean;
  retryingId: number | undefined;
}) {
  if (!overview) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
        <Spinner size="sm" decorative /> Cargando…
      </div>
    );
  }
  if (!overview.enabled) {
    return (
      <Alert tone="info" title="El envío por correo no está activado en este entorno">
        Falta la clave de envío de IDHub (IDHUB_MAIL_API_KEY).
      </Alert>
    );
  }

  const plan = planSend(overview.advisors);
  const progress = sendProgress(overview.advisors);
  const failed = overview.advisors.filter((a) => a.lastEmail?.status === 'failed' || a.lastEmail?.status === 'cancelled');
  const done = progress.sent + progress.failed + progress.cancelled;

  return (
    <div className="space-y-4">
      {overview.redirectTo && <TestModeBanner redirectTo={overview.redirectTo} />}
      <div className="flex flex-wrap items-center gap-3">
        <Button size="lg" icon={Mail} disabled={plan.recipients.length === 0 || progress.active} onClick={onSendAll}>
          Enviar a todos por correo ({plan.recipients.length})
        </Button>
        {progress.active && (
          <Button variant="danger" icon={Square} loading={stopping} onClick={onStop}>
            Detener envío
          </Button>
        )}
        {plan.withoutEmail.length > 0 && (
          <span className="text-xs text-warning-text">
            {plan.withoutEmail.length} sin email ({plan.withoutEmail.map((a) => a.fullName).join(', ')}) ·{' '}
            <Link as={RouterLink} to="/asesores" underline="always" className="font-semibold">
              añadir
            </Link>
          </span>
        )}
      </div>

      {progress.total > 0 && (
        <div className="rounded-lg border border-border p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="font-semibold">
              {progress.active ? `Enviando… ${done} de ${progress.total}` : `Enviados ${progress.sent} de ${progress.total}`}
            </span>
            <span className="text-xs text-muted-foreground">
              {progress.active && `uno cada ${overview.intervalSeconds} s · `}
              {progress.failed > 0 && (
                <span className="font-semibold text-destructive-text">{plural(progress.failed, 'no enviado', 'no enviados')}</span>
              )}
              {progress.cancelled > 0 && ` · ${plural(progress.cancelled, 'detenido', 'detenidos')}`}
            </span>
          </div>
          {/* Two-segment bar (sent / not sent): Grado's Progress has a single value. */}
          <div
            className="mt-2 flex h-2 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-label="Progreso del envío"
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={done}
          >
            <div className="bg-success transition-all" style={{ width: `${(progress.sent / progress.total) * 100}%` }} />
            <div
              className="bg-destructive transition-all"
              style={{ width: `${((progress.failed + progress.cancelled) / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {failed.length > 0 && (
        <div className="rounded-lg border border-destructive-border bg-destructive-subtle p-4">
          <h3 className="text-sm font-semibold text-destructive-text">No han recibido su informe ({failed.length})</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">Revisa el motivo y reinténtalo, o descarga su PDF para enviarlo a mano.</p>
          <ul className="mt-3 divide-y divide-destructive-border">
            {failed.map((advisor) => (
              <FailedRow
                key={advisor.id}
                advisor={advisor}
                retrying={retryingId === advisor.lastEmail!.id}
                onRetry={onRetry}
                onPreview={onPreview}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FailedRow({
  advisor,
  retrying,
  onRetry,
  onPreview,
}: {
  advisor: EmailAdvisor;
  retrying: boolean;
  onRetry: (emailId: number) => void;
  onPreview: (advisorId: number) => void;
}) {
  const email = advisor.lastEmail!;
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="text-sm font-semibold">
          {advisor.fullName} <span className="font-normal text-muted-foreground">· {email.recipient}</span>
        </div>
        <div className="text-xs text-destructive-text">
          {email.status === 'cancelled' ? 'Envío detenido antes de salir.' : describeEmailError(email.errorCode)}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {STATUS_LABEL[email.status]} · {email.attempts} {email.attempts === 1 ? 'intento' : 'intentos'} · {formatDateTime(email.createdAt)}
          {email.errorDetail && ` · ${email.errorDetail}`}
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button variant="secondary" size="sm" icon={Eye} onClick={() => onPreview(advisor.id)}>
          Ver PDF
        </Button>
        <Button size="sm" icon={RotateCw} loading={retrying} disabled={!advisor.email} onClick={() => onRetry(email.id)}>
          {retrying ? 'Reintentando…' : 'Reintentar'}
        </Button>
      </div>
    </li>
  );
}
